import React, { useState } from 'react';
import { Send, Loader2 } from 'lucide-react';
import { SignatureModal } from './SignatureModal';
import { submitProfile, getMyCompleteness, formatMissingDossierMessage, type SignatureStatus } from '@/services/validationService';
import { uploadDocument, type StudentDocumentType } from '@/services/documentsService';
import { getAllDocuments, storedDocumentToFile } from '@/services/documentStorageService';
import { UserRole } from '@/types/global.types';

interface ProfileSubmissionFlowProps {
  userRole: UserRole;
  /** Called BEFORE submission to save profile data - must complete before submitProfile() is called */
  onBeforeSubmit?: () => void | Promise<void>;
  /** Called AFTER submission completes successfully - for UI updates like locking profile */
  onSubmissionComplete: () => void | Promise<void>;
  onError: (error: string) => void;
  isDisabled?: boolean;
  isProfileComplete?: boolean;
  /**
   * Runs when the user clicks the submit button, BEFORE the règlement modal.
   * Returns the list of missing items; a non-empty list blocks the flow and
   * is reported through onError.
   */
  validateBeforeStart?: () => Promise<string[]>;
  buttonLabel?: string; // Custom button label (default: "Soumettre le profil")
  /** Signature status from previous submission - if documents already signed, skip signing modals */
  signatureStatus?: SignatureStatus;
  /** Whether this is a resubmission after rejection */
  isResubmission?: boolean;
}

type SubmissionStep = 'initial' | 'reglement' | 'consentement' | 'submitting' | 'complete';

export const ProfileSubmissionFlow: React.FC<ProfileSubmissionFlowProps> = ({
  userRole,
  onBeforeSubmit,
  onSubmissionComplete,
  onError,
  isDisabled = false,
  isProfileComplete = true,
  validateBeforeStart,
  buttonLabel = 'Soumettre le profil',
  signatureStatus,
  isResubmission = false,
}) => {
  const [currentStep, setCurrentStep] = useState<SubmissionStep>('initial');
  const [reglementVersionId, setReglementVersionId] = useState<string | null>(null);
  const [showReglementModal, setShowReglementModal] = useState(false);
  const [showConsentementModal, setShowConsentementModal] = useState(false);

  // Check if documents are already signed (from previous submission)
  const reglementAlreadySigned = signatureStatus?.reglementInterieurSigned === true;
  const consentementAlreadySigned = signatureStatus?.consentementMediasSigned === true;

  // Determine which règlement type to show based on user role
  const getReglementType = (): 'REGLEMENT_INTERIEUR_ETUDIANTS' | 'REGLEMENT_INTERIEUR_STAFF' => {
    return userRole === UserRole.STUDENT
      ? 'REGLEMENT_INTERIEUR_ETUDIANTS'
      : 'REGLEMENT_INTERIEUR_STAFF';
  };

  // Start the submission flow
  const handleStartSubmission = async () => {
    if (!isProfileComplete) {
      onError('Veuillez compléter toutes les informations requises avant de soumettre votre profil.');
      return;
    }

    if (validateBeforeStart) {
      const missing = await validateBeforeStart();
      if (missing.length > 0) {
        onError(`Votre dossier est incomplet. Éléments manquants : ${missing.join(' · ')}`);
        return;
      }
    }

    // Ask the server what still blocks the dossier BEFORE the signing steps, on
    // the values currently in the form: nobody should sign the règlement and
    // the consent only to be told afterwards that a passport is missing.
    try {
      if (onBeforeSubmit) await onBeforeSubmit();
      const completeness = await getMyCompleteness();
      if (!completeness.isComplete) {
        onError(formatMissingDossierMessage(completeness.missing));
        return;
      }
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Impossible de vérifier votre dossier. Réessayez.');
      return;
    }

    // If this is a resubmission and documents are already signed, skip directly to submission
    if (isResubmission && reglementAlreadySigned) {
      // Documents already signed - submit directly without showing modals
      doSubmitProfileResubmission();
      return;
    }

    // First time submission - show règlement modal
    setShowReglementModal(true);
    setCurrentStep('reglement');
  };

  // Handle règlement signature
  const handleReglementSign = async (versionId: string) => {
    setReglementVersionId(versionId);
    setShowReglementModal(false);
    // Show consentement modal (optional)
    setShowConsentementModal(true);
    setCurrentStep('consentement');
  };

  // Handle consentement acceptance
  const handleConsentementSign = async (versionId: string) => {
    setShowConsentementModal(false);
    // Proceed to submit
    await doSubmitProfile(reglementVersionId!, versionId, true);
  };

  // Handle consentement skip
  const handleConsentementSkip = async () => {
    setShowConsentementModal(false);
    // Proceed to submit without consentement
    await doSubmitProfile(reglementVersionId!, null, false);
  };

  // Upload all documents from IndexedDB to the backend
  const uploadDocumentsToBackend = async (): Promise<void> => {
    try {
      const storedDocs = await getAllDocuments();
      console.log('[ProfileSubmissionFlow] Found documents in IndexedDB:', storedDocs.length);

      if (storedDocs.length === 0) {
        console.log('[ProfileSubmissionFlow] No documents to upload');
        return;
      }

      // Upload each document to the backend
      for (const doc of storedDocs) {
        try {
          console.log(`[ProfileSubmissionFlow] Uploading document: ${doc.type} (${doc.fileName})`);
          const file = storedDocumentToFile(doc);
          await uploadDocument(doc.type as StudentDocumentType, file);
          console.log(`[ProfileSubmissionFlow] Successfully uploaded: ${doc.type}`);
        } catch (docError) {
          console.error(`[ProfileSubmissionFlow] Failed to upload ${doc.type}:`, docError);
          // Continue with other documents even if one fails
        }
      }

      console.log('[ProfileSubmissionFlow] All documents uploaded to backend');
    } catch (error) {
      console.error('[ProfileSubmissionFlow] Error uploading documents:', error);
      // Don't throw - allow profile submission to continue
      // Documents can be uploaded manually later if needed
    }
  };

  // Submit profile for resubmission (documents already signed)
  const doSubmitProfileResubmission = async () => {
    setCurrentStep('submitting');

    try {
      // First save profile data BEFORE submission
      if (onBeforeSubmit) {
        await onBeforeSubmit();
      }

      // Upload documents from IndexedDB to backend
      await uploadDocumentsToBackend();

      // Then submit the validation request
      // For resubmission, we don't send signature data - backend preserves original signatures
      await submitProfile({
        // No signature fields needed - backend preserves them
      });

      setCurrentStep('complete');

      // Finally update UI AFTER successful submission
      await onSubmissionComplete();
    } catch (error) {
      console.error('Error resubmitting profile:', error);
      onError(error instanceof Error ? error.message : 'Erreur lors de la re-soumission du profil');
      setCurrentStep('initial');
    }
  };

  // Submit profile to backend (first submission with new signatures)
  const doSubmitProfile = async (
    reglementVersion: string,
    consentementVersion: string | null,
    hasAcceptedConsentement: boolean
  ) => {
    setCurrentStep('submitting');

    try {
      // First save profile data BEFORE submission
      if (onBeforeSubmit) {
        await onBeforeSubmit();
      }

      // Upload documents from IndexedDB to backend
      await uploadDocumentsToBackend();

      // Then submit the validation request with signatures
      await submitProfile({
        reglementInterieurSigned: true,
        reglementInterieurVersionId: reglementVersion,
        consentementMediasAccepted: hasAcceptedConsentement,
        consentementMediasVersionId: consentementVersion || undefined,
      });

      setCurrentStep('complete');

      // Finally update UI AFTER successful submission
      await onSubmissionComplete();
    } catch (error) {
      console.error('Error submitting profile:', error);
      onError(error instanceof Error ? error.message : 'Erreur lors de la soumission du profil');
      setCurrentStep('initial');
    }
  };

  // Close modal and reset
  const handleCloseModal = () => {
    setShowReglementModal(false);
    setShowConsentementModal(false);
    setCurrentStep('initial');
  };

  // Determine button label based on resubmission state
  const getButtonLabel = () => {
    if (isResubmission && reglementAlreadySigned) {
      return 'Re-soumettre le profil';
    }
    return buttonLabel;
  };

  return (
    <>
      {/* Submit Button */}
      <button
        type="button"
        onClick={handleStartSubmission}
        disabled={isDisabled || currentStep === 'submitting'}
        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {currentStep === 'submitting' ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Soumission en cours...
          </>
        ) : (
          <>
            <Send className="w-4 h-4" />
            {getButtonLabel()}
          </>
        )}
      </button>

      {/* Règlement Intérieur Modal - Only show if not already signed */}
      {!reglementAlreadySigned && (
        <SignatureModal
          isOpen={showReglementModal}
          onClose={handleCloseModal}
          documentType={getReglementType()}
          isOptional={false}
          onSign={handleReglementSign}
        />
      )}

      {/* Consentement Médias Modal - Only show if not already signed */}
      {!consentementAlreadySigned && (
        <SignatureModal
          isOpen={showConsentementModal}
          onClose={handleCloseModal}
          documentType="CONSENTEMENT_MEDIAS"
          isOptional={true}
          onSign={handleConsentementSign}
          onSkip={handleConsentementSkip}
        />
      )}
    </>
  );
};

export default ProfileSubmissionFlow;
